import { useState, useEffect, useCallback } from 'react';
import { Sunset, Sun, Clock, Check, X, Loader2, Calendar, Zap, ChevronRight, AlertCircle } from 'lucide-react';
import { toast } from 'react-toastify';
import useAuthStore from '../stores/authStore';
import fetchWithTenant from '../utils/fetchWithTenant';

const ShabbatModeButton = () => {
  const { user, currentTenant } = useAuthStore();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalView, setModalView] = useState('choose'); // 'choose', 'on_demand_confirm', 'holiday_form', 'active_status'

  // Holiday form state
  const today = new Date().toISOString().split('T')[0];
  const [holidayStart, setHolidayStart] = useState(today);
  const [holidayEnd, setHolidayEnd] = useState('');
  const [holidayLabel, setHolidayLabel] = useState('');
  const [eveningUnlock, setEveningUnlock] = useState('18:30');
  const [eveningLock, setEveningLock] = useState('19:30');
  const [morningUnlock, setMorningUnlock] = useState('10:00');
  const [morningLock, setMorningLock] = useState('11:30');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [disableFrNow, setDisableFrNow] = useState(false);

  const canActivateShabbat = user?.canActivateShabbat || false;

  const fetchStatus = useCallback(async () => {
    if (!currentTenant?.id) return;
    try {
      const response = await fetchWithTenant('/api/shabbat/status');
      if (response.ok) {
        const data = await response.json();
        setStatus(data);
      }
    } catch (error) {
      console.error('Failed to fetch Shabbat status:', error);
    } finally {
      setLoading(false);
    }
  }, [currentTenant?.id]);

  useEffect(() => {
    if (canActivateShabbat && currentTenant?.id) {
      fetchStatus();
      const interval = setInterval(fetchStatus, 60000);
      return () => clearInterval(interval);
    } else if (!canActivateShabbat) {
      setLoading(false);
    }
  }, [canActivateShabbat, currentTenant?.id, fetchStatus]);

  const openModal = () => {
    if (status?.mode === 'ON_DEMAND' || status?.mode === 'HOLIDAY_SCHEDULE') {
      setModalView('active_status');
    } else {
      setModalView('choose');
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setModalView('choose');
    setShowAdvanced(false);
  };

  // === On Demand ===
  const handleActivateOnDemand = async () => {
    setActionLoading(true);
    try {
      const response = await fetchWithTenant('/api/shabbat/activate', { method: 'POST' });
      const data = await response.json();
      if (response.ok) {
        toast.success(data.message || 'On-demand Sabbath mode activated');
        await fetchStatus();
        setModalView('active_status');
      } else {
        toast.error(data.error || 'Failed to activate');
      }
    } catch (error) {
      toast.error('Failed to activate Sabbath mode');
    } finally {
      setActionLoading(false);
    }
  };

  // === Deactivate (any mode) ===
  const handleDeactivate = async () => {
    setActionLoading(true);
    try {
      const response = await fetchWithTenant('/api/shabbat/deactivate', { method: 'POST' });
      const data = await response.json();
      if (response.ok) {
        toast.success(data.message || 'Sabbath mode deactivated');
        await fetchStatus();
        closeModal();
      } else {
        toast.error(data.error || 'Failed to deactivate');
      }
    } catch (error) {
      toast.error('Failed to deactivate');
    } finally {
      setActionLoading(false);
    }
  };

  // === Holiday Schedule ===
  const handleActivateHoliday = async () => {
    if (!holidayStart || !holidayEnd) {
      toast.error('Please select start and end dates');
      return;
    }
    if (new Date(holidayEnd) <= new Date(holidayStart)) {
      toast.error('End date must be after start date');
      return;
    }

    setActionLoading(true);
    try {
      const response = await fetchWithTenant('/api/shabbat/activate-holiday', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          startDate: holidayStart,
          endDate: holidayEnd,
          label: holidayLabel || null,
          eveningUnlockTime: eveningUnlock,
          eveningLockTime: eveningLock,
          morningUnlockTime: morningUnlock,
          morningLockTime: morningLock,
          disableFrNow: disableFrNow,
        }),
      });
      const data = await response.json();
      if (response.ok) {
        toast.success(data.message || 'Holiday schedule activated');
        await fetchStatus();
        setModalView('active_status');
      } else {
        toast.error(data.error || 'Failed to activate holiday schedule');
      }
    } catch (error) {
      toast.error('Failed to activate holiday schedule');
    } finally {
      setActionLoading(false);
    }
  };

  // === Formatters ===
  const formatTime = (dateStr) => {
    if (!dateStr) return '--';
    return new Date(dateStr).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '--';
    return new Date(dateStr).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  const formatDateRange = (start, end) => {
    if (!start || !end) return '';
    const s = new Date(start);
    const e = new Date(end);
    const opts = { month: 'short', day: 'numeric', timeZone: 'UTC' };
    return `${s.toLocaleDateString('en-US', opts)} - ${e.toLocaleDateString('en-US', opts)}`;
  };

  const getDayCount = () => {
    if (!holidayStart || !holidayEnd) return 0;
    return Math.ceil((new Date(holidayEnd) - new Date(holidayStart)) / (1000 * 60 * 60 * 24));
  };

  // === Guard clauses ===
  if (!canActivateShabbat || !currentTenant?.id) return null;

  if (loading) {
    return (
      <div className="shabbat-container">
        <div className="shabbat-box loading">
          <Loader2 size={24} className="spin" />
          <span>Loading Sabbath status...</span>
        </div>
      </div>
    );
  }

  if (!status?.isEnabled && !status?.configured) return null;

  // === Determine button display ===
  const isActive = status?.mode === 'ON_DEMAND' || status?.mode === 'HOLIDAY_SCHEDULE';
  const modeLabel = status?.mode === 'ON_DEMAND' ? 'On Demand'
    : status?.mode === 'HOLIDAY_SCHEDULE' ? (status?.holiday?.label || 'Holiday')
    : null;

  return (
    <>
      {/* Home Screen Button */}
      <div className="shabbat-container">
        <div className={`shabbat-box ${isActive ? 'active' : ''}`}>
          <div className="shabbat-content">
            <div className="shabbat-icon">
              {isActive ? <Sunset size={28} /> : <Sun size={28} />}
            </div>
            <div className="shabbat-text">
              <h3>Sabbath Mode</h3>
              {isActive ? (
                <p className="shabbat-status-active">
                  <Check size={14} />
                  {modeLabel} Active{status?.frDisabled ? " - FR disabled" : ""}
                </p>
              ) : status?.nextJob ? (
                <p className="shabbat-next">
                  <Clock size={14} /> Next: {status.nextJob.action.replace(/\(.*\)/, '').trim()} at {formatTime(status.nextJob.scheduledFor)}
                </p>
              ) : (
                <p>Automated weekly schedule</p>
              )}
            </div>
          </div>

          <div className="shabbat-actions">
            {!isActive && status?.nextShabbat?.start && (
              <div className="shabbat-schedule">
                <span className="shabbat-schedule-label">Starts:</span>
                <span className="shabbat-schedule-time">
                  {formatDate(status.nextShabbat.start)} {formatTime(status.nextShabbat.start)}
                </span>
              </div>
            )}
            {isActive && status?.mode === 'HOLIDAY_SCHEDULE' && status?.holiday && (
              <div className="shabbat-schedule">
                <span className="shabbat-schedule-label">{status.holiday.label || 'Holiday'}</span>
                <span className="shabbat-schedule-time">
                  {formatDateRange(status.holiday.startDate, status.holiday.endDate)}
                </span>
              </div>
            )}
            <button
              className={`btn shabbat-btn ${isActive ? 'btn-warning' : 'btn-primary'}`}
              onClick={openModal}
            >
              {isActive ? (
                <><Sun size={18} /> Manage</>
              ) : (
                <><Sunset size={18} /> Activate</>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="sabbath-overlay" onClick={closeModal}>
          <div className="sabbath-scroll-wrap" onClick={closeModal}>
            <div className="sabbath-modal" onClick={e => e.stopPropagation()}>
            <button className="modal-close" onClick={closeModal}>
              <X size={24} />
            </button>

            {/* === CHOOSE MODE VIEW === */}
            {modalView === 'choose' && (
              <>
                <div className="sabbath-modal-header">
                  <Sunset size={36} color="#8b5cf6" />
                  <h2>Sabbath Mode</h2>
                  <p className="sabbath-modal-subtitle">Choose how to activate</p>
                </div>

                <div className="sabbath-mode-cards">
                  {/* On Demand Card */}
                  <button
                    className="sabbath-mode-card"
                    onClick={() => setModalView('on_demand_confirm')}
                  >
                    <div className="sabbath-mode-card-icon" style={{ background: 'rgba(139, 92, 246, 0.15)' }}>
                      <Zap size={24} color="#a78bfa" />
                    </div>
                    <div className="sabbath-mode-card-text">
                      <h3>On Demand</h3>
                      <p>Activate immediately. FR disables now, doors follow the regular service schedule. Stays active until you turn it off.</p>
                    </div>
                    <ChevronRight size={20} className="sabbath-mode-card-arrow" />
                  </button>

                  {/* Holiday Card */}
                  <button
                    className="sabbath-mode-card"
                    onClick={() => setModalView('holiday_form')}
                  >
                    <div className="sabbath-mode-card-icon" style={{ background: 'rgba(251, 191, 36, 0.15)' }}>
                      <Calendar size={24} color="#fbbf24" />
                    </div>
                    <div className="sabbath-mode-card-text">
                      <h3>Holiday Schedule</h3>
                      <p>Set a date range. FR disabled for the duration, doors unlock on schedule. Auto-deactivates when the holiday ends.</p>
                    </div>
                    <ChevronRight size={20} className="sabbath-mode-card-arrow" />
                  </button>
                </div>
              </>
            )}

            {/* === ON DEMAND CONFIRM VIEW === */}
            {modalView === 'on_demand_confirm' && (
              <>
                <div className="sabbath-modal-header">
                  <Zap size={36} color="#a78bfa" />
                  <h2>On Demand</h2>
                </div>

                <div className="sabbath-confirm-details">
                  <div className="sabbath-detail-row">
                    <AlertCircle size={16} color="#a78bfa" />
                    <span>Facial recognition will be <strong>disabled immediately</strong></span>
                  </div>
                  <div className="sabbath-detail-row">
                    <Clock size={16} color="#a78bfa" />
                    <span>{status?.frontDoor?.name || 'Front door'} unlocks <strong>6:30 - 7:30 PM</strong> (evening service)</span>
                  </div>
                  <div className="sabbath-detail-row">
                    <Clock size={16} color="#a78bfa" />
                    <span>{status?.frontDoor?.name || 'Front door'} unlocks <strong>10:00 - 11:30 AM</strong> (morning service)</span>
                  </div>
                  <div className="sabbath-detail-row">
                    <Sun size={16} color="#fbbf24" />
                    <span>You will receive a <strong>nightly reminder</strong> while active</span>
                  </div>
                </div>

                <div className="sabbath-modal-actions">
                  <button className="btn btn-secondary" onClick={() => setModalView('choose')}>
                    Back
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={handleActivateOnDemand}
                    disabled={actionLoading}
                  >
                    {actionLoading ? (
                      <><Loader2 size={18} className="spin" /> Activating...</>
                    ) : (
                      <><Zap size={18} /> Activate Now</>
                    )}
                  </button>
                </div>
              </>
            )}

            {/* === HOLIDAY FORM VIEW === */}
            {modalView === 'holiday_form' && (
              <>
                <div className="sabbath-modal-header">
                  <Calendar size={36} color="#fbbf24" />
                  <h2>Holiday Schedule</h2>
                </div>

                <div className="sabbath-holiday-form">
                  <div className="sabbath-form-group">
                    <label>Holiday Name (optional)</label>
                    <input
                      type="text"
                      className="form-input"
                      placeholder="e.g. Passover, Sukkot"
                      value={holidayLabel}
                      onChange={e => setHolidayLabel(e.target.value)}
                    />
                  </div>

                  <div className="sabbath-form-row">
                    <div className="sabbath-form-group">
                      <label>Start Date</label>
                      <input
                        type="date"
                        className="form-input"
                        value={holidayStart}
                        min={today}
                        onChange={e => setHolidayStart(e.target.value)}
                      />
                    </div>
                    <div className="sabbath-form-group">
                      <label>End Date</label>
                      <input
                        type="date"
                        className="form-input"
                        value={holidayEnd}
                        min={holidayStart || today}
                        onChange={e => setHolidayEnd(e.target.value)}
                      />
                    </div>
                  </div>

                  {holidayStart && holidayEnd && getDayCount() > 0 && (
                    <div className="sabbath-day-count">
                      {getDayCount()} day{getDayCount() !== 1 ? 's' : ''} - FR disabled for the duration, doors unlock on schedule
                    </div>
                  )}

                  <label className="sabbath-fr-now-toggle">
                    <input
                      type="checkbox"
                      checked={disableFrNow}
                      onChange={e => setDisableFrNow(e.target.checked)}
                    />
                    <span>Disable facial recognition immediately</span>
                  </label>

                  <button
                    className="sabbath-advanced-toggle"
                    onClick={() => setShowAdvanced(!showAdvanced)}
                  >
                    {showAdvanced ? 'Hide' : 'Customize'} door schedule times
                    <ChevronRight size={16} className={showAdvanced ? 'rotated' : ''} />
                  </button>

                  {showAdvanced && (
                    <div className="sabbath-time-grid">
                      <div className="sabbath-time-section">
                        <span className="sabbath-time-label">Evening Service</span>
                        <div className="sabbath-form-row">
                          <div className="sabbath-form-group">
                            <label>Unlock</label>
                            <input type="time" className="form-input" value={eveningUnlock} onChange={e => setEveningUnlock(e.target.value)} />
                          </div>
                          <div className="sabbath-form-group">
                            <label>Lock</label>
                            <input type="time" className="form-input" value={eveningLock} onChange={e => setEveningLock(e.target.value)} />
                          </div>
                        </div>
                      </div>
                      <div className="sabbath-time-section">
                        <span className="sabbath-time-label">Morning Service</span>
                        <div className="sabbath-form-row">
                          <div className="sabbath-form-group">
                            <label>Unlock</label>
                            <input type="time" className="form-input" value={morningUnlock} onChange={e => setMorningUnlock(e.target.value)} />
                          </div>
                          <div className="sabbath-form-group">
                            <label>Lock</label>
                            <input type="time" className="form-input" value={morningLock} onChange={e => setMorningLock(e.target.value)} />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="sabbath-modal-actions">
                  <button className="btn btn-secondary" onClick={() => setModalView('choose')}>
                    Back
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={handleActivateHoliday}
                    disabled={actionLoading || !holidayStart || !holidayEnd}
                  >
                    {actionLoading ? (
                      <><Loader2 size={18} className="spin" /> Creating Schedule...</>
                    ) : (
                      <><Calendar size={18} /> Activate Holiday</>
                    )}
                  </button>
                </div>
              </>
            )}

            {/* === ACTIVE STATUS VIEW === */}
            {modalView === 'active_status' && (
              <>
                <div className="sabbath-modal-header">
                  {status?.mode === 'ON_DEMAND' ? (
                    <Zap size={36} color="#a78bfa" />
                  ) : (
                    <Calendar size={36} color="#fbbf24" />
                  )}
                  <h2>
                    {status?.mode === 'ON_DEMAND' ? 'On Demand Active' : `${status?.holiday?.label || 'Holiday'} Active`}
                  </h2>
                </div>

                <div className="sabbath-active-info">
                  <div className={`sabbath-status-badge ${status?.frDisabled ? 'active' : 'scheduled'}`}>
                    {status?.frDisabled ? (
                      <><Check size={14} /> Facial Recognition Disabled</>
                    ) : (
                      <><Clock size={14} /> FR Disables at Sunset</>
                    )}
                  </div>

                  {status?.mode === 'HOLIDAY_SCHEDULE' && status?.holiday && (
                    <div className="sabbath-holiday-info">
                      <div className="sabbath-info-row">
                        <span className="sabbath-info-label">Dates</span>
                        <span>{formatDateRange(status.holiday.startDate, status.holiday.endDate)}</span>
                      </div>
                      <div className="sabbath-info-row">
                        <span className="sabbath-info-label">Evening Door</span>
                        <span>{status.holiday.eveningUnlock} - {status.holiday.eveningLock}</span>
                      </div>
                      <div className="sabbath-info-row">
                        <span className="sabbath-info-label">Morning Door</span>
                        <span>{status.holiday.morningUnlock} - {status.holiday.morningLock}</span>
                      </div>
                      {status.holiday.jobsRemaining > 0 && (
                        <div className="sabbath-info-row">
                          <span className="sabbath-info-label">Remaining</span>
                          <span>{status.holiday.jobsRemaining} scheduled actions</span>
                        </div>
                      )}
                    </div>
                  )}

                  {status?.mode === 'ON_DEMAND' && (
                    <div className="sabbath-holiday-info">
                      <div className="sabbath-info-row">
                        <span className="sabbath-info-label">Evening Door</span>
                        <span>6:30 - 7:30 PM daily</span>
                      </div>
                      <div className="sabbath-info-row">
                        <span className="sabbath-info-label">Morning Door</span>
                        <span>10:00 - 11:30 AM daily</span>
                      </div>
                      <div className="sabbath-info-row">
                        <span className="sabbath-info-label">Reminder</span>
                        <span>Nightly at 30 min after sunset</span>
                      </div>
                    </div>
                  )}

                  {status?.nextJob && (
                    <div className="sabbath-next-action">
                      <Clock size={14} />
                      <span>Next: {status.nextJob.action.replace(/\(.*\)/, '').trim()}</span>
                      <span className="sabbath-next-time">{formatDate(status.nextJob.scheduledFor)} {formatTime(status.nextJob.scheduledFor)}</span>
                    </div>
                  )}
                </div>

                <div className="sabbath-modal-actions">
                  <button className="btn btn-secondary" onClick={closeModal}>
                    Close
                  </button>
                  <button
                    className="btn btn-warning"
                    onClick={handleDeactivate}
                    disabled={actionLoading}
                  >
                    {actionLoading ? (
                      <><Loader2 size={18} className="spin" /> Deactivating...</>
                    ) : (
                      <><Sun size={18} /> {status?.mode === 'HOLIDAY_SCHEDULE' ? 'Cancel Holiday' : 'Deactivate'}</>
                    )}
                  </button>
                </div>
              </>
            )}

            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ShabbatModeButton;
